import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

// File system types
export interface FileNode {
  name: string;
  type: 'file' | 'directory';
  content?: string; // For files
  children?: string[]; // For directories - names of children
  permissions: string;
}

export interface FileSystem {
  [path: string]: FileNode;
}

// Home directory constant
export const HOME_DIR = '/home/visitor';

// Initial file system structure
const INITIAL_FILE_SYSTEM: FileSystem = {
  '/home/visitor': {
    name: 'visitor',
    type: 'directory',
    children: ['README.md', 'secret_folder', '.ssh'],
    permissions: 'drwxr-xr-x',
  },
  '/home/visitor/README.md': {
    name: 'README.md',
    type: 'file',
    content: `# Hi there.
    
Welcome.`,
    permissions: '-rw-r--r--',
  },
  '/home/visitor/secret_folder': {
    name: 'secret_folder',
    type: 'directory',
    children: ['dont_do_it.txt'],
    permissions: 'drwxr-xr-x',
  },
  '/home/visitor/secret_folder/dont_do_it.txt': {
    name: 'dont_do_it.txt',
    type: 'file',
    content: `you did it.`,
    permissions: '-rw-r--r--',
  },
  '/home/visitor/.ssh': {
    name: '.ssh',
    type: 'directory',
    children: ['id_ed25519', 'id_ed25519.pub'],
    permissions: 'drwx------',
  },
  '/home/visitor/.ssh/id_ed25519': {
    name: 'id_ed25519',
    type: 'file',
    content: `lol`,
    permissions: '-rw-------',
  },
  '/home/visitor/.ssh/id_ed25519.pub': {
    name: 'id_ed25519.pub',
    type: 'file',
    content: `id_ed25519 AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA visitor@nickboz.dev`,
    permissions: '-rw-r--r--',
  },
};

// Context type
interface FileSystemContextType {
  files: FileSystem;
  currentPath: string;
  setCurrentPath: (path: string) => void;
  readFile: (path: string) => string | null;
  writeFile: (path: string, content: string) => boolean;
  createFile: (path: string, name: string, content?: string) => boolean;
  deleteFile: (path: string) => boolean;
  listDirectory: (path: string) => FileNode[] | null;
  getNode: (path: string) => FileNode | null;
  resolvePath: (path: string, currentDir?: string) => string;
  getParentPath: (path: string) => string;
}

const FileSystemContext = createContext<FileSystemContextType | null>(null);

// Helper to normalize paths
function normalizePath(path: string): string {
  // Remove trailing slash unless it's root
  if (path.length > 1 && path.endsWith('/')) {
    path = path.slice(0, -1);
  }
  return path;
}

// Provider component
export function FileSystemProvider({ children }: { children: ReactNode }) {
  const [files, setFiles] = useState<FileSystem>(INITIAL_FILE_SYSTEM);
  const [currentPath, setCurrentPath] = useState(HOME_DIR);

  const getParentPath = useCallback((path: string): string => {
    const normalized = normalizePath(path);
    const lastSlash = normalized.lastIndexOf('/');
    // Allow going above HOME_DIR so terminal can detect and show permission denied
    if (lastSlash <= 0) return '/';
    return normalized.slice(0, lastSlash) || '/';
  }, []);

  const resolvePath = useCallback((path: string, currentDir?: string): string => {
    const base = currentDir || currentPath;

    if (path === '~' || path === '') {
      return HOME_DIR;
    }

    if (path.startsWith('/')) {
      return normalizePath(path);
    }

    if (path === '..') {
      return getParentPath(base);
    }

    if (path === '.') {
      return normalizePath(base);
    }

    // Handle relative paths with ..
    const parts = path.split('/');
    let result = base;

    for (const part of parts) {
      if (part === '..') {
        result = getParentPath(result);
      } else if (part !== '.' && part !== '') {
        result = `${result}/${part}`;
      }
    }

    return normalizePath(result);
  }, [currentPath, getParentPath]);

  const getNode = useCallback((path: string): FileNode | null => {
    const resolved = resolvePath(path);
    return files[resolved] || null;
  }, [files, resolvePath]);

  const readFile = useCallback((path: string): string | null => {
    const node = getNode(path);
    if (node && node.type === 'file') {
      return node.content || '';
    }
    return null;
  }, [getNode]);

  const writeFile = useCallback((path: string, content: string): boolean => {
    const resolved = resolvePath(path);
    const node = files[resolved];

    if (node && node.type === 'file') {
      setFiles(prev => ({
        ...prev,
        [resolved]: {
          ...prev[resolved],
          content,
        },
      }));
      return true;
    }
    return false;
  }, [files, resolvePath]);

  const createFile = useCallback((dirPath: string, name: string, content: string = ''): boolean => {
    const resolvedDir = resolvePath(dirPath);
    const dir = files[resolvedDir];

    if (!dir || dir.type !== 'directory') {
      return false;
    }

    const newFilePath = `${resolvedDir}/${name}`;

    // Check if file already exists
    if (files[newFilePath]) {
      return false;
    }

    setFiles(prev => ({
      ...prev,
      [resolvedDir]: {
        ...prev[resolvedDir],
        children: [...(prev[resolvedDir].children || []), name],
      },
      [newFilePath]: {
        name,
        type: 'file',
        content,
        permissions: '-rw-r--r--',
      },
    }));

    return true;
  }, [files, resolvePath]);

  const deleteFile = useCallback((path: string): boolean => {
    const resolved = resolvePath(path);
    const node = files[resolved];

    if (!node) {
      return false;
    }

    // Get parent directory
    const parentPath = getParentPath(resolved);
    const parent = files[parentPath];

    if (!parent || parent.type !== 'directory') {
      return false;
    }

    setFiles(prev => {
      const newFiles = { ...prev };

      // Remove from parent's children
      newFiles[parentPath] = {
        ...newFiles[parentPath],
        children: (newFiles[parentPath].children || []).filter(c => c !== node.name),
      };

      // Delete the file/directory
      delete newFiles[resolved];

      // If it's a directory, delete all children recursively
      if (node.type === 'directory') {
        Object.keys(newFiles).forEach(key => {
          if (key.startsWith(resolved + '/')) {
            delete newFiles[key];
          }
        });
      }

      return newFiles;
    });

    return true;
  }, [files, resolvePath, getParentPath]);

  const listDirectory = useCallback((path: string): FileNode[] | null => {
    const resolved = resolvePath(path);
    const dir = files[resolved];

    if (!dir || dir.type !== 'directory') {
      return null;
    }

    const children = dir.children || [];
    return children.map(name => {
      const childPath = `${resolved}/${name}`;
      return files[childPath];
    }).filter(Boolean) as FileNode[];
  }, [files, resolvePath]);

  const value: FileSystemContextType = {
    files,
    currentPath,
    setCurrentPath,
    readFile,
    writeFile,
    createFile,
    deleteFile,
    listDirectory,
    getNode,
    resolvePath,
    getParentPath,
  };

  return (
    <FileSystemContext.Provider value={value}>
      {children}
    </FileSystemContext.Provider>
  );
}

// Hook to use the file system
export function useFileSystem(): FileSystemContextType {
  const context = useContext(FileSystemContext);
  if (!context) {
    throw new Error('useFileSystem must be used within a FileSystemProvider');
  }
  return context;
}
